import { Dispatch } from 'redux';

import { FileObj } from 'components/FileUploads';
import { buildEVisitData } from 'lib/evisit/utils';
import {
  AdapterSource,
  DFDHeadersObject,
  DispatcherAuthPrefix,
  DispatcherCategoryService,
  DispatcherEndpoint,
  DispatcherResource
} from 'modules/types/common';
import { getErrorMessage } from 'services/api/utils';
import { isDfdAuthenticatedSelector } from 'store/authentication/selectors';
import { getSessionToken } from 'store/triageGyant/actions';
import {
  triageGyantCurrentCaseIdSelector,
  triageGyantSessionTokenDataSelector
} from 'store/triageGyant/selectors';
import { TriagePatient, TriageProxy } from 'store/triageGyant/types';
import { getSymptomCheckerService } from 'store/triageGyant/utils';
import { RootState, Thunk } from 'store/types';
import { getDispatcherUrl } from 'modules/utils/UrlUtils';
import { DFDHeaders } from 'serverTypes/authentication';
import {
  EVisitFormData,
  InsuranceFile,
  InsuranceFileState,
  UploadInsuranceFilePayload,
  UploadInsuranceFileResponse,
  InsurancesUpload,
  SubscriberDetailsDTO
} from './types';
import { navigateToEVisit } from './actions';
import { mapInsuranceCards } from './utils';

export const RESET_E_VISIT_SESSION_TOKEN = 'E_VISIT.RESET_E_VISIT_SESSION_TOKEN';
export const SET_E_VISIT_SESSION_TOKEN = 'E_VISIT.SET_E_VISIT_SESSION_TOKEN';

export const setEVisitSessionToken = (token: string, options?: object) => ({
  type: SET_E_VISIT_SESSION_TOKEN,
  payload: { token, options }
});

export const SEND_E_VISIT_DATA = 'E_VISIT.SEND_E_VISIT_DATA';
export const SEND_E_VISIT_DATA_SUCCESS = 'E_VISIT.SEND_E_VISIT_DATA_SUCCESS';
export const SEND_E_VISIT_DATA_FAIL = 'E_VISIT.SEND_E_VISIT_DATA_FAIL';
export const sendEVisitFormData = (
  caseId: string,
  data: EVisitFormData
): Thunk => async dispatch => {
  const CASE_DATA_URL = getDispatcherUrl(
    DispatcherCategoryService.DELIVER_CARE,
    DispatcherAuthPrefix.AUTHENTICATED,
    DispatcherResource.SYMPTOM_CHECKER,
    DispatcherEndpoint.CASE_DATA
  );
  const { SC_EXPANSION_ENABLED, client, url } = getSymptomCheckerService(
    '/case-data',
    CASE_DATA_URL
  );
  const headers: DFDHeadersObject = {};
  if (SC_EXPANSION_ENABLED) {
    headers['mhp-adapter'] = 'mhp';
    headers['mhp-filter-resource-id'] = caseId;
  } else {
    headers[DFDHeaders.caseId] = caseId;
  }

  return dispatch({
    type: SEND_E_VISIT_DATA,
    payload: {
      client,
      request: {
        url,
        method: 'post',
        headers,
        data
      }
    }
  });
};

export const START_E_VISIT = 'E_VISIT.START_E_VISIT';
export const START_E_VISIT_SUCCESS = 'E_VISIT.START_E_VISIT_SUCCESS';
export const START_E_VISIT_FAIL = 'E_VISIT.START_E_VISIT_FAIL';
export const startEVisitSession = (selectedLocationState: string): Thunk => async (
  dispatch: Dispatch,
  getState: () => RootState
) => {
  try {
    dispatch({ type: START_E_VISIT });

    const state = getState();
    const caseId = triageGyantCurrentCaseIdSelector(state);
    const sessionToken = triageGyantSessionTokenDataSelector(state);
    const data = buildEVisitData(state, selectedLocationState);
    const result = await dispatch(sendEVisitFormData(caseId, data));

    if (result.error) {
      return dispatch({ type: START_E_VISIT_FAIL });
    }

    await dispatch(setEVisitSessionToken(sessionToken));
    navigateToEVisit(dispatch);
    return dispatch({ type: START_E_VISIT_SUCCESS, payload: selectedLocationState });
  } catch (err) {
    return dispatch({ type: START_E_VISIT_FAIL });
  }
};

export const startEVisitFrontDoorSession = (
  patient: TriagePatient | TriageProxy,
  selectedLocationState: string
): Thunk => async (dispatch: Dispatch, getState: () => RootState) => {
  try {
    dispatch({ type: START_E_VISIT });

    const state = getState();
    const sessionTokenResponse = await dispatch(getSessionToken(patient));

    if (sessionTokenResponse.error) {
      return dispatch({ type: START_E_VISIT_FAIL });
    }

    const { sessionToken, userId } = sessionTokenResponse.payload.data;
    const data = buildEVisitData(state, selectedLocationState);
    const result = await dispatch(sendEVisitFormData(userId, data));

    if (result.error) {
      return dispatch({ type: START_E_VISIT_FAIL });
    }

    await dispatch(setEVisitSessionToken(sessionToken, { isFrontDoor: true }));
    navigateToEVisit(dispatch);
    return dispatch({ type: START_E_VISIT_SUCCESS, payload: selectedLocationState });
  } catch (err) {
    return dispatch({ type: START_E_VISIT_FAIL });
  }
};

// Check EVisit eligibility
export const CHECK_E_VISIT_ELIGIBILITY = 'E_VISIT.CHECK_E_VISIT_ELIGIBILITY';
export const CHECK_E_VISIT_ELIGIBILITY_SUCCESS = 'E_VISIT.CHECK_E_VISIT_ELIGIBILITY_SUCCESS';
export const CHECK_E_VISIT_ELIGIBILITY_FAIL = 'E_VISIT.CHECK_E_VISIT_ELIGIBILITY_FAIL';
export const checkEVisitEligibility = (caseId: string): Thunk => async (
  dispatch: Dispatch,
  getState: () => RootState
) => {
  const isAuthenticated = isDfdAuthenticatedSelector(getState());
  if (!isAuthenticated) {
    return dispatch({
      type: CHECK_E_VISIT_ELIGIBILITY_FAIL,
      error: new Error('Guest user ineligible for E-Visit')
    });
  }
  const ELIGIBILITY_URL = getDispatcherUrl(
    DispatcherCategoryService.DELIVER_CARE,
    DispatcherAuthPrefix.AUTHENTICATED,
    DispatcherResource.SYMPTOM_CHECKER,
    DispatcherEndpoint.ELIGIBILITY
  );
  const { SC_EXPANSION_ENABLED, client, url } = getSymptomCheckerService(
    '/eligibility/exclusion-criteria',
    ELIGIBILITY_URL
  );
  const headers: DFDHeadersObject = {};
  if (SC_EXPANSION_ENABLED) {
    headers['mhp-adapter'] = 'mhp';
  } else {
    headers['x-case-id'] = caseId;
  }

  return dispatch({
    type: CHECK_E_VISIT_ELIGIBILITY,
    payload: {
      client,
      request: {
        url,
        method: 'get',
        headers
      }
    }
  });
};

// Check EVisit eligibility
export const CHECK_E_VISIT_SUMMARY = 'E_VISIT.SUMMARY';
export const CHECK_E_VISIT_SUMMARY_SUCCESS = 'E_VISIT.SUMMARY_SUCCESS';
export const CHECK_E_VISIT_SUMMARY_FAIL = 'E_VISIT.SUMMARY_FAIL';
export const checkEVisitSummary = (): Thunk => async (dispatch: Dispatch) => {
  const url = getDispatcherUrl(
    DispatcherCategoryService.CONSUMER,
    DispatcherAuthPrefix.AUTHENTICATED,
    DispatcherResource.CONSUMERS_ID_INSURANCE,
    DispatcherEndpoint.SUMMARY
  );
  const headers = {
    [DFDHeaders.mhpAdapter]: AdapterSource.MHP
  };

  return dispatch({
    type: CHECK_E_VISIT_SUMMARY,
    payload: {
      client: 'dispatcher',
      request: {
        url,
        method: 'get',
        headers
      }
    }
  });
};

// Send EVisit Insurance
export const SEND_E_VISIT_INSURANCE = 'E_VISIT.SEND_INSURANCE';
export const SEND_E_VISIT_INSURANCE_SUCCESS = 'E_VISIT.SEND_INSURANCE_SUCCESS';
export const SEND_E_VISIT_INSURANCE_FAIL = 'E_VISIT.SEND_INSURANCE_FAIL';
export const sendInsuranceEVisit = (subscriberDetails: SubscriberDetailsDTO): Thunk => async (
  dispatch: Dispatch,
  getState: () => RootState
) => {
  const state = getState();
  const data: InsurancesUpload = { subscriberDetails, insuranceCards: mapInsuranceCards(state) };

  const url = getDispatcherUrl(
    DispatcherCategoryService.DELIVER_CARE,
    DispatcherAuthPrefix.AUTHENTICATED,
    DispatcherResource.INSURANCES
  );
  const headers = {
    [DFDHeaders.mhpAdapter]: AdapterSource.MHP
  };

  return dispatch({
    type: SEND_E_VISIT_INSURANCE,
    payload: {
      client: 'dispatcher',
      request: {
        url,
        method: 'post',
        headers,
        data
      }
    }
  });
};

export const resetEVisitSessionToken = (): Thunk => async dispatch => {
  dispatch({ type: RESET_E_VISIT_SESSION_TOKEN });
};

export const GET_USER_INSURANCE_INFO = 'E_VISIT.GET_USER_INSURANCE_INFO';
export const GET_USER_INSURANCE_INFO_SUCCESS = 'E_VISIT.GET_USER_INSURANCE_INFO_SUCCESS';
export const GET_USER_INSURANCE_INFO_FAIL = 'E_VISIT.GET_USER_INSURANCE_INFO_FAIL';
export const getUserInsuranceInfo = (): Thunk => async (
  dispatch: Dispatch,
  getState: () => RootState
) => {
  const state = getState();
  const isAuthenticated = isDfdAuthenticatedSelector(state);

  if (!isAuthenticated) {
    return dispatch({
      type: GET_USER_INSURANCE_INFO_FAIL,
      error: new Error('Guest user ineligible for E-Visit')
    });
  }

  const url = getDispatcherUrl(
    DispatcherCategoryService.CONSUMER,
    DispatcherAuthPrefix.AUTHENTICATED,
    DispatcherResource.CONSUMERS,
    DispatcherEndpoint.ID_INSURANCE_SUMMARY
  );
  const headers: DFDHeadersObject = {
    [DFDHeaders.mhpAdapter]: AdapterSource.MHP
  };

  return dispatch({
    type: GET_USER_INSURANCE_INFO,
    payload: {
      client: 'dispatcher',
      request: {
        method: 'get',
        url,
        headers
      }
    }
  });
};

export const SET_INSURANCE_FILE = 'E_VISIT.SET_INSURANCE_FILE';

export const setInsuranceFile = (file: InsuranceFile) => ({
  type: SET_INSURANCE_FILE,
  payload: file
});

export const RESET_INSURANCE_FILE = 'E_VISIT.RESET_INSURANCE_FILE';
export const RESET_INSURANCE_FILES = 'E_VISIT.RESET_INSURANCE_FILES';
export const resetInsuranceFiles = () => ({
  type: RESET_INSURANCE_FILES
});

export const resetInsuranceFile = (insuranceType: keyof InsuranceFileState) => ({
  type: RESET_INSURANCE_FILE,
  payload: {
    file: null,
    insuranceType,
    attachmentId: null,
    azureFileName: null,
    status: 'pending',
    errorMessage: null,
    isFetching: false
  }
});

export const UPLOAD_INSURANCE_FILE = `E_VISIT.UPLOAD_INSURANCE_FILE`;
export const UPLOAD_INSURANCE_FILE_SUCCESS = `E_VISIT.UPLOAD_INSURANCE_FILE_SUCCESS`;
export const UPLOAD_INSURANCE_FILE_FAIL = `E_VISIT.UPLOAD_INSURANCE_FILE_FAIL`;

export const uploadInsuranceFile = (
  file: FileObj,
  insuranceType: keyof InsuranceFileState,
  uuid: string
): Thunk<Promise<UploadInsuranceFileResponse>> => async dispatch => {
  const formData = new FormData();
  formData.append(insuranceType, file);

  await dispatch(
    setInsuranceFile({
      file: null,
      attachmentId: null,
      azureFileName: null,
      errorMessage: null,
      insuranceType,
      isFetching: true,
      status: 'processing'
    })
  );

  const result = ((await dispatch({
    type: UPLOAD_INSURANCE_FILE,
    payload: {
      client: 'dispatcher',
      request: {
        method: 'post',
        headers: {
          [DFDHeaders.contentType]: 'multipart/form-data',
          [DFDHeaders.mhpAdapter]: AdapterSource.MHP,
          [DFDHeaders.mhpMessageUuid]: uuid
        },
        url: getDispatcherUrl(
          DispatcherCategoryService.DELIVER_CARE,
          DispatcherAuthPrefix.PUBLIC,
          DispatcherResource.INSURANCES,
          DispatcherEndpoint.UPLOAD
        ),
        data: formData
      }
    }
  })) as unknown) as UploadInsuranceFilePayload;

  if (result.error) {
    const error = getErrorMessage(result.error);
    await dispatch(
      setInsuranceFile({
        isFetching: false,
        insuranceType,
        attachmentId: null,
        azureFileName: null,
        file: null,
        status: 'error',
        errorMessage: error
      })
    );
    return { error };
  }

  const { attachmentId, azureFileName } = result.payload?.data as InsuranceFile;
  await dispatch(
    setInsuranceFile({
      isFetching: false,
      insuranceType,
      attachmentId,
      azureFileName,
      file,
      status: 'success',
      errorMessage: null
    })
  );

  return {
    attachmentId: result.payload?.data?.attachmentId,
    azureFileName: result.payload?.data?.azureFileName
  };
};
